import {
  bootstrapSupabaseConfig,
  getSupabase,
  isSupabaseConfigured,
  onSupabaseClientReady,
} from './client';
import { LEARNER_TABLES } from './tables';

/** @typedef {{ pageKey: string; clapCount: number }} PageClapStatsUpdate */

/** @typedef {{
 *   id?: string;
 *   pageKey: string;
 *   pageTitle: string | null;
 *   actorUserId: string | null;
 *   clapDelta: number;
 *   userClapTotal: number;
 *   pageClapTotal: number;
 * }} PageClapEvent */

/** @typedef {{
 *   id: string;
 *   pageKey: string;
 *   pageTitle: string | null;
 *   actorUserId: string | null;
 *   pageClapTotal: number;
 * }} ClapActivityEvent */

/** @typedef {{
 *   pageKey: string;
 *   pageTitle: string | null;
 *   actorUserId: string | null;
 *   pageClapTotal: number;
 * }} ClapActivityBroadcast
 */

const CLAP_BROADCAST_CHANNEL = 'richuals-clap-activity';
const RETRY_MS = 2000;
const MAX_ATTEMPTS = 30;
const CLAP_POLL_MS = 2500;

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {PageClapStatsUpdate | null}
 */
function parseStatsRow(row) {
  if (!row?.page_key) return null;
  return {
    pageKey: String(row.page_key),
    clapCount: Number(row.clap_count ?? 0),
  };
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {PageClapEvent | null}
 */
function parseEventRow(row) {
  if (!row?.page_key) return null;
  return {
    id: row.id != null ? String(row.id) : undefined,
    pageKey: String(row.page_key),
    pageTitle: row.page_title != null ? String(row.page_title) : null,
    actorUserId: row.actor_user_id != null ? String(row.actor_user_id) : null,
    clapDelta: Number(row.clap_delta ?? 1),
    userClapTotal: Number(row.user_clap_total ?? 0),
    pageClapTotal: Number(row.page_clap_total ?? 0),
  };
}

/**
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {ClapActivityEvent | null}
 */
function parseActivityEventRow(row) {
  if (!row?.id || !row?.page_key) return null;
  return {
    id: String(row.id),
    pageKey: String(row.page_key),
    pageTitle: row.page_title != null ? String(row.page_title) : null,
    actorUserId: row.actor_user_id != null ? String(row.actor_user_id) : null,
    pageClapTotal: Number(row.page_clap_total ?? 0),
  };
}

/**
 * @returns {Promise<boolean>}
 */
export async function ensureSupabaseReady() {
  if (!isSupabaseConfigured()) {
    await bootstrapSupabaseConfig();
  }
  return isSupabaseConfigured() && Boolean(getSupabase());
}

/**
 * @param {import('@supabase/supabase-js').RealtimeChannel} channel
 * @param {string} label
 */
function subscribeChannel(channel, label) {
  channel.subscribe((status, err) => {
    if (status === 'SUBSCRIBED') {
      console.warn(`[pageClapRealtime] ${label}: subscribed`);
      return;
    }
    if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
      console.warn(`[pageClapRealtime] ${label}: ${status}`, err?.message ?? '');
    }
  });
}

/**
 * @param {(client: import('@supabase/supabase-js').SupabaseClient) => () => void} bindChannel
 * @param {string} label
 * @returns {() => void}
 */
function subscribeWhenReady(bindChannel, label) {
  let active = true;
  let teardown = () => {};
  let boundClient = null;

  const attach = (/** @type {import('@supabase/supabase-js').SupabaseClient} */ client) => {
    if (!active) return;
    if (boundClient === client && teardown) return;
    teardown();
    boundClient = client;
    teardown = bindChannel(client);
  };

  const detach = () => {
    teardown();
    teardown = () => {};
    boundClient = null;
  };

  void (async () => {
    for (let attempt = 0; attempt < MAX_ATTEMPTS && active; attempt += 1) {
      const ready = await ensureSupabaseReady();
      if (!active) return;
      if (ready) {
        const client = getSupabase();
        if (!client || !active) return;
        attach(client);
        return;
      }
      await new Promise((resolve) => {
        window.setTimeout(resolve, RETRY_MS);
      });
    }
    if (active) {
      console.warn(`[pageClapRealtime] ${label}: Supabase not ready; live updates disabled`);
    }
  })();

  const stopClientListener = onSupabaseClientReady((client) => {
    if (!active) return;
    attach(client);
  });

  return () => {
    active = false;
    stopClientListener();
    detach();
  };
}

/**
 * Poll recent clap events - reliable fallback when Realtime broadcast/postgres_changes lag.
 * @param {number} [limit]
 * @returns {Promise<ClapActivityEvent[]>}
 */
export async function fetchRecentClapEvents(limit = 8) {
  const ready = await ensureSupabaseReady();
  if (!ready) return [];

  const client = getSupabase();
  if (!client) return [];

  const { data, error } = await client
    .from(LEARNER_TABLES.pageClapEvents)
    .select('id, page_key, page_title, page_clap_total, actor_user_id, created_at')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.warn('[pageClapRealtime] poll failed:', error.message);
    return [];
  }

  return (data ?? [])
    .map((row) => parseActivityEventRow(row))
    .filter((row) => row != null);
}

/**
 * Poll clap events and invoke callback for rows not yet seen.
 * @param {(event: ClapActivityEvent) => void} onEvent
 * @returns {() => void}
 */
export function startClapEventPolling(onEvent) {
  let active = true;
  /** @type {Set<string>} */
  const seenIds = new Set();
  let primed = false;

  const tick = async () => {
    if (!active) return;
    const events = await fetchRecentClapEvents(10);
    if (!active) return;

    if (!primed) {
      events.forEach((event) => seenIds.add(event.id));
      primed = true;
      return;
    }

    events
      .slice()
      .reverse()
      .forEach((event) => {
        if (seenIds.has(event.id)) return;
        seenIds.add(event.id);
        onEvent(event);
      });

    if (seenIds.size > 250) {
      const keep = new Set(events.map((event) => event.id));
      seenIds.forEach((id) => {
        if (!keep.has(id)) seenIds.delete(id);
      });
    }
  };

  void tick();
  const intervalId = window.setInterval(() => {
    void tick();
  }, CLAP_POLL_MS);

  return () => {
    active = false;
    window.clearInterval(intervalId);
  };
}

/**
 * Fan-out clap activity to every connected browser (any page, signed-in or not).
 * Called by the client that just clapped, after the RPC succeeds.
 * @param {ClapActivityBroadcast} payload
 */
export async function broadcastClapActivity(payload) {
  const ready = await ensureSupabaseReady();
  if (!ready || !payload?.pageKey) {
    console.warn('[pageClapRealtime] broadcast skipped: Supabase not ready or missing pageKey');
    return;
  }

  const client = getSupabase();
  if (!client) return;

  const channel = client.channel(CLAP_BROADCAST_CHANNEL, {
    config: { broadcast: { ack: false, self: false }, private: true },
  });

  const subscribed = await new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(false), 5000);
    channel.subscribe((status, err) => {
      if (status === 'SUBSCRIBED') {
        window.clearTimeout(timer);
        resolve(true);
        return;
      }
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        window.clearTimeout(timer);
        console.warn('[pageClapRealtime] broadcast send channel:', status, err?.message ?? '');
        resolve(false);
      }
    });
  });

  if (!subscribed) {
    client.removeChannel(channel);
    return;
  }

  const result = await channel.send({
    type: 'broadcast',
    event: 'clap',
    payload: {
      pageKey: payload.pageKey,
      pageTitle: payload.pageTitle ?? null,
      pageClapTotal: payload.pageClapTotal,
      actorUserId: payload.actorUserId ?? null,
    },
  });

  if (result !== 'ok') {
    console.warn('[pageClapRealtime] broadcast send failed:', result);
  }

  client.removeChannel(channel);
}

/**
 * Listen for clap activity from any user on any page.
 * @param {(event: ClapActivityBroadcast) => void} onBroadcast
 * @returns {() => void}
 */
export function subscribeClapActivityBroadcast(onBroadcast) {
  return subscribeWhenReady((client) => {
    const channel = client
      .channel(CLAP_BROADCAST_CHANNEL, {
        config: { broadcast: { self: false }, private: true },
      })
      .on('broadcast', { event: 'clap' }, ({ payload }) => {
        if (!payload?.pageKey) return;
        onBroadcast({
          pageKey: String(payload.pageKey),
          pageTitle: payload.pageTitle != null ? String(payload.pageTitle) : null,
          actorUserId: payload.actorUserId != null ? String(payload.actorUserId) : null,
          pageClapTotal: Number(payload.pageClapTotal ?? 0),
        });
      });

    subscribeChannel(channel, 'broadcast');

    return () => {
      client.removeChannel(channel);
    };
  }, 'broadcast');
}

/**
 * @param {(update: PageClapStatsUpdate) => void} onStatsChange
 * @returns {() => void}
 */
export function subscribePageClapStats(onStatsChange) {
  return subscribeWhenReady((client) => {
    const channel = client
      .channel('page-clap-stats')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: LEARNER_TABLES.pageClapStats },
        (payload) => {
          const parsed = parseStatsRow(payload.new);
          if (parsed) onStatsChange(parsed);
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: LEARNER_TABLES.pageClapStats },
        (payload) => {
          const parsed = parseStatsRow(payload.new);
          if (parsed) onStatsChange(parsed);
        },
      );

    subscribeChannel(channel, 'stats');

    return () => {
      client.removeChannel(channel);
    };
  }, 'stats');
}

/**
 * @param {(event: PageClapEvent) => void} onClapEvent
 * @returns {() => void}
 */
export function subscribePageClapEvents(onClapEvent) {
  return subscribeWhenReady((client) => {
    const channel = client
      .channel('page-clap-events')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: LEARNER_TABLES.pageClapEvents },
        (payload) => {
          const parsed = parseEventRow(payload.new);
          if (!parsed) return;
          onClapEvent({
            id: parsed.id,
            pageKey: parsed.pageKey,
            pageTitle: parsed.pageTitle,
            actorUserId: parsed.actorUserId,
            pageClapTotal: parsed.pageClapTotal,
          });
        },
      );

    subscribeChannel(channel, 'events');

    return () => {
      client.removeChannel(channel);
    };
  }, 'events');
}
